import { useState, useEffect } from 'react'
import { IslandLabel } from '@/components/IslandLabel'
import { SLIDE_NAME_ISLAND } from '@/components/islandPresets'
import type { Media } from '@/types/api'
import { getAuthHeaders } from '@/lib/auth-token'

export interface BoxCounts {
  gt: number
  rejection: number
  suggestion: number
}

interface MediaCardProps {
  media: Media
  organizationId?: string
  isSelected?: boolean
  /** Per-category annotation counts, rendered as chips (skill grid). */
  counts?: BoxCounts
  /** Chip click — e.g. open the slide with that category's filter active. */
  onChipClick?: (kind: keyof BoxCounts) => void
  style?: React.CSSProperties
  onClick?: () => void
}

// Category rows (lab variant D, 2026-09-18): color dot + label + count, the
// yellow tint on hover tying into the rail filter buttons' yellow=active
// language. The suggestion marker is a tiny dotted square — the rail filter's
// dotted-box glyph at 10px (8 evenly tiled dots: each corner + each side's middle).
const ROW_META: Record<keyof BoxCounts, { label: string; dot?: React.CSSProperties }> = {
  gt: { label: 'Ground truth', dot: { background: 'var(--accent-green)' } },
  rejection: { label: 'Rejected', dot: { background: 'var(--accent-red)' } },
  suggestion: { label: 'Suggestions' },
}

// currentColor: follows the row's text color, in both themes and on the yellow hover
function DottedMarker() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeDasharray="0 1" className="shrink-0" aria-hidden="true">
      <rect x="1" y="1" width="8" height="8" pathLength={8} />
    </svg>
  )
}

function useAuthenticatedImage(url: string | null): string | undefined {
  const [blobUrl, setBlobUrl] = useState<string>()

  useEffect(() => {
    if (!url) return
    let cancelled = false

    fetch(url, { headers: getAuthHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`)
        return res.blob()
      })
      .then((blob) => {
        if (!cancelled) setBlobUrl(URL.createObjectURL(blob))
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(undefined)
      })

    return () => {
      cancelled = true
    }
  }, [url])

  return blobUrl
}

export function MediaCard({ media, organizationId, isSelected, counts, onChipClick, style, onClick }: MediaCardProps) {
  const mediaVersionId = media.media_versions?.[0]?.id
  const thumbnailPath = organizationId && mediaVersionId
    ? `/data/${organizationId}/${mediaVersionId}/thumbnail.jpg`
    : null
  const thumbnailUrl = useAuthenticatedImage(thumbnailPath)

  const hasValidThumbnail = !!thumbnailUrl
  const filename = media.media_path?.match(/\/([^/]+)$/)?.[1] ?? media.media_path ?? ''

  const card = (
    <button
      type="button"
      onClick={onClick}
      style={style}
      className={`
        group relative h-[156px] w-[156px] cursor-pointer overflow-hidden rounded-sm transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-fill-primary focus:ring-inset
        ${isSelected ? '' : 'hover:ring-2 hover:ring-border-strong hover:ring-inset'}
      `}
    >
      {hasValidThumbnail ? (
        <img
          src={thumbnailUrl}
          alt={`Media ${media.id}`}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="chrome-panel flex h-full w-full items-center justify-center border border-border-default/40">
          <span className="text-caption-small text-text-tertiary"></span>
        </div>
      )}

      {/* Slide name: micro chip at rest; rolling over the chip itself (not the whole
          card — that made the rail too busy) grows it Dynamic-Island style into a
          readable pill, and a name that still doesn't fit scrolls. */}
      <IslandLabel {...SLIDE_NAME_ISLAND} text={filename} />

      {/* Selection state: eo-blue ring + inner glow, as an overlay ABOVE the
          image — inset box-shadows on the button itself paint beneath child
          content, so a ring on the button is invisible behind the thumbnail. */}
      {isSelected && (
        <div
          className="pointer-events-none absolute inset-0 rounded-sm"
          style={{
            boxShadow:
              'inset 0 0 0 2px var(--accent-blue), inset 0 0 18px color-mix(in srgb, var(--accent-blue) 55%, transparent)',
          }}
        />
      )}
    </button>
  )

  if (!counts) return card

  // Grid layout: category rows BELOW the thumbnail (lab variant D), giving the
  // image full visibility and the card a readable, clickable footer.
  return (
    <div className="flex w-[156px] flex-col gap-1.5">
      {card}
      <div className="flex w-full flex-col gap-0.5">
        {(Object.keys(ROW_META) as (keyof BoxCounts)[]).map((kind) =>
          counts[kind] > 0 ? (
            <button
              key={kind}
              type="button"
              onClick={onChipClick ? () => onChipClick(kind) : undefined}
              title={`Open with only ${ROW_META[kind].label.toLowerCase()} shown`}
              className="flex w-full items-center gap-2 rounded-md border border-transparent px-2 py-1 text-xs font-semibold text-text-primary transition-colors hover:border-[color-mix(in_srgb,var(--accent-yellow)_60%,transparent)] hover:bg-[color-mix(in_srgb,var(--accent-yellow)_28%,transparent)]"
              style={{ cursor: onChipClick ? 'pointer' : 'default' }}
            >
              {ROW_META[kind].dot ? (
                <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={ROW_META[kind].dot} />
              ) : (
                <DottedMarker />
              )}
              {ROW_META[kind].label}
              <span className="ml-auto font-medium tabular-nums text-text-secondary">
                {counts[kind]}
              </span>
            </button>
          ) : null,
        )}
      </div>
    </div>
  )
}
